import { Doc, generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  EnrollmentFormDenormalized,
  EnrollmentFormSubmissionDTO,
  SubmitFormResponse
} from '@dadohr/shared/domainTypes/enrollmentForms';
import {
  EventChannel,
  IExperienceDenormalized,
  IExperienceDenormalizedCommon,
  IExperienceDenormalizedStatuses,
  isSubTaskBlock,
  ITaskDenormalized,
  SubTaskQuestionAnswer,
  SubTaskQuestionAnswerAddress,
  SubTaskQuestionAnswerLinearScale,
  SubTaskQuestionAnswerMultiSelect,
  SubTaskQuestionAnswerRating,
  SubTaskQuestionAnswerSelect,
  SubTaskQuestionAnswerSupportingActor,
  SubTaskQuestionAnswerText,
  SubTaskType,
  SwagUpChoiceAnswerData,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import {
  PAEventType,
  ProductAnalyticsEvent
} from '@dadohr/shared/domainTypes/productAnalytics';
import { SsoProviderType } from '@dadohr/shared/domainTypes/sso';
import { ISubmitFileMonitor } from '@dadohr/shared/domainTypes/submitFileMonitor';
import { FS } from '@dadohr/shared/versions';
import firebase from 'firebase/app';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef } from 'react';
import shortid from 'shortid';
import ENV from '../../../env.json';
import { postAnswerSubTasks } from './answerSubTasksRequest';
import { usePromise } from '../../../hooks/usePromise';
import { useTranslation } from '../../../i18next';
import {
  LoadingValue,
  LoadingValueLike,
  refreshTimestamp,
  store,
  useCombineLoadingValues,
  useMappedLoadingValue
} from '../../../services/firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';
import * as stackdriver from '../../../stackdriverErrorReporter';
import { Completions, SubTaskContext } from './components/Task';
import { DEV_FIXTURE } from './fixture';
import {
  isImpersonationMode,
  standaloneFetch
} from '../../services/standaloneApi';

const toDenormalizedStatusesDoc = generateToDocFn<IExperienceDenormalizedStatuses>(
  (d) => {
    d.p = d.p || null;
    return d;
  }
);
const statusStore = createDocumentListenerGetter(
  (accessToken) =>
    store().collection(FS.experienceDenormalizedStatuses).doc(accessToken),
  toDenormalizedStatusesDoc
);

const refreshDenormalization = (
  d: Doc<IExperienceDenormalized>
): Doc<IExperienceDenormalized> => {
  return {
    ...d,
    data: {
      ...d.data,
      startsAt: refreshTimestamp(d.data.startsAt),
      user: {
        ...d.data.user
        // hireDate: refreshTimestamp(d.data.user.hireDate),
        // terminationDate: refreshTimestamp(d.data.user.terminationDate)
      },
      tasks: d.data.tasks.map((t) => ({
        ...t,
        start: refreshTimestamp(t.start),
        end: refreshTimestamp(t.end),
        completedAt: refreshTimestamp(t.completedAt),
        subTasks: (t.subTasks || []).map((s) => {
          if (s.type === 'QUESTION') {
            return {
              ...s,
              data: {
                ...s.data,
                answer: s.data?.answer
                  ? {
                      ...s.data.answer,
                      createdAt: refreshTimestamp(s.data.answer?.createdAt),
                      editedAt: refreshTimestamp(s.data.answer?.editedAt)
                    }
                  : null
              }
            };
          }
          if (s.type === 'CALENDLY_INVITE' && s.data?.booking) {
            s.data.booking.startTime = refreshTimestamp(
              s.data.booking.startTime
            );
            s.data.booking.endTime = refreshTimestamp(s.data.booking.endTime);
          }
          return s;
        }),
        blocks: (t.blocks || []).map((b) => {
          if (b.type === 'QUESTION') {
            return {
              ...b,
              data: {
                ...b.data,
                answer: b.data.answer
                  ? {
                      ...b.data.answer,
                      createdAt: refreshTimestamp(b.data.answer?.createdAt),
                      editedAt: refreshTimestamp(b.data.answer?.editedAt)
                    }
                  : null
              }
            };
          }
          if (b.type === 'CALENDLY_INVITE' && b.data?.booking) {
            b.data.booking.startTime = refreshTimestamp(
              b.data.booking.startTime
            );
            b.data.booking.endTime = refreshTimestamp(b.data.booking.endTime);
          }
          return b;
        }),
        customCompletionCriteria: t.customCompletionCriteria || []
      })),
      messages: (d.data.messages || []).map((m) => {
        return {
          ...m,
          sendAt: refreshTimestamp(m.sendAt)
        };
      })
    }
  };
};

export const useStandaloneExperienceDev = (
  accessToken: string
): LoadingValue<Doc<IExperienceDenormalized>> => {
  const value = useMemo(() => refreshDenormalization(DEV_FIXTURE), []);
  return [value, false, undefined];
};

export const useStandaloneExperienceStatus = (accessToken: string) => {
  return useDocumentListener(statusStore(accessToken));
};

// Direct Cloud Functions URLs on localhost AND in safe-impersonation mode (the admin
// origin has no /api/* rewrites for employee-app endpoints). See services/standaloneApi.ts.
const isLocalhost = () =>
  isImpersonationMode() || window.location.host.startsWith('localhost');

const getDenormalizedExperience = (accessToken: string) => {
  const url = new URL(
    isLocalhost() ? ENV.domains.cf.public : window.location.origin
  );
  url.pathname = isLocalhost()
    ? 'standaloneApp_getExperience2'
    : '/api/getExperience';
  url.searchParams.set('accessToken', accessToken);
  url.searchParams.set('currentUrl', window.location.href);
  return standaloneFetch(url.toString(), {
    redirect: 'follow',
    headers: {
      Accept: 'application/json'
      // Authorization: authToken ? `Bearer ${authToken}` : ''
    }
  }).then(async (r) => {
    console.log('response', r);
    if (
      !isImpersonationMode() &&
      r.status === 404 &&
      (window.location.origin.startsWith('http://localhost:3115') ||
        window.location.origin.startsWith(
          'https://emplexp-dev-standalone.firebaseapp.com'
        ))
    ) {
      window.location.href = `https://my.dadohr.com${window.location.pathname}`;
    }
    const d:
      | {
          status: 'SSO_REDIRECT';
          location: string;
        }
      | {
          status: 'SSO_WRONG_USER_IN_SESSION';
          sessionEmail: string;
          ssoProvider: SsoProviderType;
        }
      | {
          status: 'OK';
          experience: Doc<IExperienceDenormalized>;
        } = await r.json();
    return new Promise<{
      status: 'OK';
      experience: Doc<IExperienceDenormalized>;
    }>((resolve, reject) => {
      if (d.status === 'SSO_REDIRECT') {
        window.location.href = d.location;
      } else if (d.status === 'SSO_WRONG_USER_IN_SESSION') {
        reject(d);
      } else {
        resolve(d);
      }
    });
  });
};

const useDenormalizedExperience = (accessToken: string, v: number) => {
  return usePromise(() => getDenormalizedExperience(accessToken), [
    accessToken,
    v
  ]);
};

export const refreshAndAggregateDenormalizedExperience = (
  instance: Doc<IExperienceDenormalized>,
  statusDoc: Doc<IExperienceDenormalizedStatuses> | null
) => {
  const doc = refreshDenormalization(instance);
  if (statusDoc) {
    doc.data.status = statusDoc.data.s;
    doc.data.tasks.forEach((t) => {
      const ts = statusDoc.data.ts[t.id];
      t.status = ts?.s || t.status;
      (t.subTasks || []).forEach((s) => {
        const ss = ts?.ss[s.id];
        s.status = ss?.s || s.status;
      });
      (t.blocks || []).forEach((b) => {
        if (isSubTaskBlock(b)) {
          const ss = ts?.ss[b.id];
          b.status = ss?.s || b.status;
          b.lastCompletedAt = refreshTimestamp(b.lastCompletedAt);
        }
      });
    });
    doc.data.messages.forEach((m) => {
      m.markedAsRead = m.markedAsRead
        ? {
            at: refreshTimestamp(m.markedAsRead.at),
            read: m.markedAsRead.read
          }
        : null;
    });
  }
  return doc;
};

/**
 * @deprecated use V1OrV2 fn instead
 */
export const useStandaloneExperience = (
  accessToken: string,
  v = 0
): LoadingValue<Doc<IExperienceDenormalized>> => {
  return useMappedLoadingValue(
    useCombineLoadingValues(
      useDenormalizedExperience(accessToken, v),
      useStandaloneExperienceStatus(accessToken),
      { ignoreIntermediaryLoadingState: true }
    ),
    ([res, statusDoc]) => {
      return refreshAndAggregateDenormalizedExperience(
        res.experience,
        statusDoc
      );
    }
  );
};

export const useStackdriverSetup = (
  experience: void | Doc<IExperienceDenormalized>
) => {
  useEffect(() => {
    stackdriver.setUser(experience?.data.userId || '');
  }, [experience]);
};

export const useAnalyticsSetup = (
  experience: void | Doc<IExperienceDenormalized>
) => {
  useEffect(() => {
    firebase.analytics().setUserId(experience?.data.userId || '');
  }, [experience]);
};

export const trackSubTaskCompletion = (
  experienceId: string,
  taskId: string,
  subTaskId: string,
  subTaskType: SubTaskType
) => {
  return firebase.analytics().logEvent('subtask_complete', {
    experience_id: experienceId,
    task_id: taskId,
    subtask_id: subTaskId,
    subtask_type: subTaskType
  });
};

export const NOT_FOUND_MESSAGES = new Set([
  'NO_EXPERIENCE',
  'NO_USER',
  'NO_ORG'
]);

export const archiveMessage = async (
  instanceAccessToken: string,
  msgId: string,
  nextArchiveState: boolean
) => {
  const u = new URL(ENV.domains.cf.public);
  const x: {
    a: string;
    m: string;
    v: boolean;
  } = {
    a: instanceAccessToken,
    m: msgId,
    v: nextArchiveState
  };
  const d = btoa(JSON.stringify(x, null, 0));
  u.pathname = '/experiences_markMessageAsReadFromEmployeeApp';
  u.searchParams.set('d', d);
  const url = u.toString();
  const res = await standaloneFetch(url);
  const result: { status: 'OK' } = await res.json();
  return result;
};

export const getSubTaskCompletionUrl = (
  experienceId: string,
  taskId: string,
  subTaskId: string
) => {
  const u = new URL(ENV.domains.cf.public);
  const x: {
    e: string;
    t: string;
    s: string;
    r?: string;
    c: EventChannel;
  } = {
    e: experienceId,
    t: taskId,
    s: subTaskId,
    c: EventChannel.APP
  };
  const d = btoa(JSON.stringify(x, null, 0));
  // Reclaimed hyphen name (new-main-39o0): the completion fn was renamed
  // experiences_completeSubTask → experiences-completeSubTask so v1 emails already in
  // inboxes resolve. Both names can't co-exist (one Cloud Run service), so the app
  // moves to the hyphen too. Deploy this together with the function rename.
  u.pathname = '/experiences-completeSubTask';
  u.searchParams.set('d', d);
  return u.toString();
};

export const completeSubTask = async (
  experienceId: string,
  taskId: string,
  subTaskId: string
): Promise<{
  status: 'OK';
  completions: {
    tasks: { taskId: string; taskTemplateId: string }[];
    subTasks: {
      taskId: string;
      taskTemplateId: string;
      subTaskId: string;
      subTaskTemplateId: string;
    }[];
  };
}> => {
  const res = await standaloneFetch(
    getSubTaskCompletionUrl(experienceId, taskId, subTaskId)
  );
  const result = await res.json();
  return result;
};

// SC-32079: the answer payload builder + POST live in the dependency-light
// ./answerSubTasksRequest module so they are unit-testable without importing this
// heavy service module. `answerSubTasks` below delegates to it.

const getCompleteComplexSubTaskUrl = (
  experienceId: string,
  taskId: string,
  subTaskId: string,
  d: SubTaskQuestionAnswer['d']
) => {
  const u = new URL(ENV.domains.cf.public);
  const x: {
    e: string;
    t: string;
    s: string;
    c: EventChannel;
    d: SubTaskQuestionAnswer['d'];
  } = {
    e: experienceId,
    t: taskId,
    s: subTaskId,
    c: EventChannel.APP,
    d
  };
  const data = btoa(unescape(encodeURIComponent(JSON.stringify(x, null, 0))));
  u.pathname = '/experiences_completeComplexSubTask';
  u.searchParams.set('d', data);
  return u.toString();
};

export type AnswerSubTasksResponse = {
  status: 'OK' | 'SKIPPED';
  experienceId: string;
  taskId: string;
  answers: { subTaskId: string; d: SubTaskQuestionAnswer['d'] }[];
  completions: {
    tasks: { taskId: string; taskTemplateId: string }[];
    subTasks: {
      taskId: string;
      taskTemplateId: string;
      subTaskId: string;
      subTaskTemplateId: string;
    }[];
  };
};
export const answerSubTasks = async (
  experienceId: string,
  taskId: string,
  answers: { subTaskId: string; d: SubTaskQuestionAnswer['d'] }[]
): Promise<AnswerSubTasksResponse> => {
  if (!answers.length) {
    return {
      status: 'SKIPPED',
      answers,
      experienceId,
      taskId,
      completions: { tasks: [], subTasks: [] }
    };
  }
  // SC-32079: POST via the extracted request module (no base64 GET URL-length cap).
  const res = await postAnswerSubTasks(
    ENV.domains.cf.public,
    experienceId,
    taskId,
    answers
  );
  return {
    status: 'OK',
    answers,
    experienceId,
    taskId,
    completions: res?.completions || { tasks: [], subTasks: [] }
  };
};

export const answerSubTask = async (
  experienceId: string,
  taskId: string,
  subTaskId: string,
  answer: SubTaskQuestionAnswer['d']
) => {
  return answerSubTasks(experienceId, taskId, [{ subTaskId, d: answer }]);
};

export const completeComplexSubTask = async (
  experienceId: string,
  taskId: string,
  subTaskId: string,
  answer: SubTaskQuestionAnswer['d']
): Promise<
  | {
      status: 'OK';
      completions: {
        tasks: { taskId: string; taskTemplateId: string }[];
        subTasks: {
          taskId: string;
          taskTemplateId: string;
          subTaskId: string;
          subTaskTemplateId: string;
        }[];
      };
      data?: string;
    }
  | {
      status: 'ERROR';
      reason: string;
      data?: any;
    }
> => {
  const res = await standaloneFetch(
    getCompleteComplexSubTaskUrl(experienceId, taskId, subTaskId, answer)
  ).then((r) => r.json());
  return res;
};

export const getSubTaskVisitUrl = (
  url: string,
  experienceId: string,
  taskId: string,
  subTaskId: string
) => {
  const u = new URL(ENV.domains.cf.public);
  // Must match the deployed function + all server-side builders (hyphen). The
  // underscore name 404s: it sanitizes to the same Cloud Run service as
  // `redirect-redirect`, so the two can't coexist (see functions/redirect).
  u.pathname = '/redirect-redirect';
  const x: {
    t: string;
    p: {
      e: string;
      t: string;
      s: string;
    };
  } = {
    t: 'exp-subTask-visit',
    p: {
      e: experienceId,
      t: taskId,
      s: subTaskId
    }
  };
  const d = btoa(JSON.stringify(x, null, 0));
  u.searchParams.set('url', encodeURIComponent(url));
  u.searchParams.set('d', d);
  return u.toString();
};

const toSubmitFileMonitorDoc = generateToDocFn<ISubmitFileMonitor>((d) => {
  d.originalFileName = d.originalFileName || null;
  return d;
});

const submitFileMonitorStore = createDocumentListenerGetter(
  (id) => store().collection(FS.submitFileMonitor).doc(id),
  toSubmitFileMonitorDoc
);

export const useSubmitFileMonitorDoc = (id: string) => {
  return useDocumentListener(submitFileMonitorStore(id));
};

export const removeSubmitFileMonitorDoc = (id: string) => {
  return store().collection(FS.submitFileMonitor).doc(id).delete();
};

export const useTaskCompletionSuccessNotifications = <
  T extends { tasks: ITaskDenormalized[] }
>(
  exp: void | Doc<T>
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const ref = useRef(exp);
  useEffect(() => {
    const prev = ref.current;
    ref.current = exp;
    if (!exp || !prev) {
      return;
    }
    const completedTasks: ITaskDenormalized[] = [];
    const prevTaskStates: { [taskId: string]: TaskStatus } = {};
    prev.data.tasks.forEach((task) => (prevTaskStates[task.id] = task.status));
    exp.data.tasks.forEach((task) => {
      if (
        task.status === TaskStatus.COMPLETE &&
        prevTaskStates[task.id] !== TaskStatus.COMPLETE
      ) {
        completedTasks.push(task);
      }
    });
    completedTasks.forEach((task) =>
      enqueueSnackbar(
        t('standaloneApp.taskCompletedSnack', {
          taskTitle: task.title
        }),
        { variant: 'success' }
      )
    );
  }, [exp, t, enqueueSnackbar]);
};

const getEnrollmentForm = (accessToken: string) => {
  const url = new URL(
    isLocalhost() ? ENV.domains.cf.public : window.location.origin
  );

  url.pathname = isLocalhost()
    ? 'standaloneApp_getEnrollmentFormDenormalized'
    : '/api/getEnrollmentFormDenormalized';
  url.searchParams.set('accessToken', accessToken);
  url.searchParams.set('currentUrl', window.location.href);
  return standaloneFetch(url.toString(), {
    redirect: 'follow',
    headers: {
      Accept: 'application/json'
      // Authorization: authToken ? `Bearer ${authToken}` : ''
    }
  }).then(async (r) => {
    console.log('response', r);
    if (
      !isImpersonationMode() &&
      r.status === 404 &&
      (window.location.origin.startsWith('http://localhost:3115') ||
        window.location.origin.startsWith(
          'https://emplexp-dev-standalone.firebaseapp.com'
        ))
    ) {
      window.location.href = `https://my.dadohr.com${window.location.pathname}`;
    }
    const d:
      | {
          status: 'SSO_REDIRECT';
          location: string;
        }
      | {
          status: 'SSO_WRONG_USER_IN_SESSION';
          sessionEmail: string;
          ssoProvider: SsoProviderType;
        }
      | {
          status: 'OK';
          enrollmentForm: EnrollmentFormDenormalized;
        } = await r.json();
    return new Promise<EnrollmentFormDenormalized>((resolve, reject) => {
      if (d.status === 'SSO_REDIRECT') {
        window.location.href = d.location;
      } else if (d.status === 'SSO_WRONG_USER_IN_SESSION') {
        reject(d);
      } else {
        resolve(d.enrollmentForm);
      }
    });
  });
};

export const useEnrollmentForm = (
  accessToken: string
): LoadingValueLike<EnrollmentFormDenormalized> =>
  usePromise(() => getEnrollmentForm(accessToken), [accessToken]);

const getSubmitEnrollmentFormsUrl = (
  formSubmission: EnrollmentFormSubmissionDTO
) => {
  const u = new URL(ENV.domains.cf.public);

  const d = btoa(
    unescape(encodeURIComponent(JSON.stringify(formSubmission, null, 0)))
  );
  u.pathname = '/standaloneApp_submmitEnrollmentForm';
  u.searchParams.set('d', d);
  // new-main-oydj: send currentUrl so an SSO-required form that bounces to
  // SSO_REDIRECT on submit can return the user to this page post-login (the
  // server builds the IdP login URL from it). Matches getDenormalizedExperience
  // + getEnrollmentForm, which already set it.
  u.searchParams.set('currentUrl', window.location.href);
  return u.toString();
};
export const submitEnrollmentForm = async (
  formSubmission: EnrollmentFormSubmissionDTO
) => {
  const url = getSubmitEnrollmentFormsUrl(formSubmission);
  const response = await standaloneFetch(url);
  return (await response.json()) as SubmitFormResponse;
};

export const getSubTaskContext = (
  taskId: string,
  d: Doc<IExperienceDenormalizedCommon>,
  trackFn: (event: ProductAnalyticsEvent) => void
): SubTaskContext => ({
  tz: d.data.org.tz,
  user: d.data.user,
  userId: d.data.userId,
  submitFileMonitorId: (subTaskId: string) =>
    `${d.data.expId}-${taskId}-${subTaskId}-${d.data.userId}`,

  completeSubTask: (subTaskId: string) => {
    return completeSubTask(d.data.expId, taskId, subTaskId);
  },
  trackSubTaskCompletion: (subTaskId: string, subTaskType: SubTaskType) => {
    return trackSubTaskCompletion(d.data.expId, taskId, subTaskId, subTaskType);
  },
  constructNextFileSubmitMonitor: (
    file: File,
    subTaskId: string
  ): ISubmitFileMonitor => {
    const extMatch = file.name.match(/.+?\.(.+)/);
    return {
      experienceId: d.data.expId,
      taskId: taskId,
      subTaskId: subTaskId,
      storageRef: `uploads/${
        d.data.userId
      }/${shortid()}${shortid()}${shortid()}${
        extMatch ? `.${extMatch[1]}` : ''
      }`,
      originalFileName: file.name,
      attempts: 0,
      status: 'UPLOAD'
    };
  },
  getSubTaskVisitUrl: (subtaskId: string, url: string): string => {
    return getSubTaskVisitUrl(url, d.data.expId, taskId, subtaskId);
  },
  answerSubTasks: (
    answers: {
      subTaskId: string;
      d:
        | SubTaskQuestionAnswerText
        | SubTaskQuestionAnswerAddress
        | SubTaskQuestionAnswerSelect
        | SubTaskQuestionAnswerMultiSelect
        | SwagUpChoiceAnswerData
        | SubTaskQuestionAnswerSupportingActor
        | SubTaskQuestionAnswerLinearScale
        | SubTaskQuestionAnswerRating;
    }[]
  ) => {
    return answerSubTasks(d.data.expId, taskId, answers);
  },
  answerSubTask: (
    subtaskId: string,
    value:
      | SubTaskQuestionAnswerText
      | SubTaskQuestionAnswerAddress
      | SubTaskQuestionAnswerSelect
      | SubTaskQuestionAnswerMultiSelect
      | SwagUpChoiceAnswerData
      | SubTaskQuestionAnswerSupportingActor
      | SubTaskQuestionAnswerLinearScale
      | SubTaskQuestionAnswerRating
  ) => {
    return answerSubTask(d.data.expId, taskId, subtaskId, value);
  },
  answerComplexSubtask: function (
    subtaskId: string,
    value:
      | SubTaskQuestionAnswerText
      | SubTaskQuestionAnswerAddress
      | SubTaskQuestionAnswerSelect
      | SubTaskQuestionAnswerMultiSelect
      | SwagUpChoiceAnswerData
      | SubTaskQuestionAnswerSupportingActor
      | SubTaskQuestionAnswerLinearScale
      | SubTaskQuestionAnswerRating
  ) {
    return completeComplexSubTask(d.data.expId, taskId, subtaskId, value);
  },
  trackEvent: trackFn
});

export const trackCompletions = (
  completions: Completions | undefined,
  trackEvent: (event: ProductAnalyticsEvent) => void,
  opts: { intentOnly?: boolean } = {}
) => {
  completions?.subTasks?.forEach((x) => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_SUB_TASK,
      name: 'Sub-Task Completed',
      Explanation: opts.intentOnly
        ? `An assignee took the first step towards completing the sub-task, which is all that's trackable in the front-end`
        : 'A user completed a sub-task',
      'Task ID': x.taskId,
      'Sub-Task ID': x.subTaskId
    });
  });
  completions?.tasks?.forEach((tsk) => {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_TASK,
      name: 'Task Completed',
      Explanation: 'A user completed a task',
      'Task ID': tsk.taskId
    });
  });
};

export const trackCompletionIntent = (
  taskId: string,
  subTaskId: string,
  trackEvent: (event: ProductAnalyticsEvent) => void
) => {
  // Moments can call this with nothing, as we don't want to track this at this point.
  if (taskId && subTaskId) {
    trackEvent({
      eventType: PAEventType.EMPLOYEE_EXPERIENCE_SUB_TASK,
      name: 'Sub-Task Completed',
      Explanation: `An assignee took the first step towards completing the sub-task, which is all that's trackable in the front-end`,
      'Task ID': taskId,
      'Sub-Task ID': subTaskId
    });
  }
};
